import moment from 'moment/moment'
import { v4 as uuidv4 } from 'uuid'
import * as yup from 'yup'
import { IEventPaymentSettingsInterface } from '../event-payment-settings/event-payment-settings.interface'
import { selectObjToString } from '../interface.helper'
import { getIncrement } from '../../helpers/payment'
import { IModelBaseModel } from '../model-base/model-base.interface'
import { ModelBaseModel } from '../model-base/model-base.model'
import {
  BeddingFeeType,
  FeedFeesType,
  GovernanceFeesType,
  IEventFees,
  OtherFeeType,
  QualifyingClassesType,
  RefundsType,
  RegistrationFeesType,
  RegistrationLimitType,
  RideTimeRequirementType,
  StallFeesType,
} from './event-fees.interface'

export class EventFeesModel extends ModelBaseModel<IEventFees> implements IModelBaseModel {
  public v: number
  public id: IEventFees['id']
  public owner: IEventFees['owner']

  public registrationFees: RegistrationFeesType[]
  public paymentOption: IEventPaymentSettingsInterface['paymentOption'] | null

  public showCancellationRefundWeather: RefundsType
  public showCancellationRefundBioSecurity: RefundsType
  public afterClosingDateRefund: RefundsType
  public beforeClosingDateRefund: RefundsType
  public extraRefunds: RefundsType[] | any

  public temporaryStallsFee: StallFeesType
  public permanentStallsFee: StallFeesType
  public privateStallsFee: StallFeesType
  public circuitStallsFee: StallFeesType
  public muckFee: StallFeesType
  public extraStallFees: StallFeesType[] | any

  public USEFFee: GovernanceFeesType
  public USEFDrugFee: GovernanceFeesType
  public USEFShowPassFee: GovernanceFeesType
  public USDFFee: GovernanceFeesType
  public extraGovernanceFees: GovernanceFeesType[] | any

  public paddockFee: OtherFeeType
  public RVHookupFee: OtherFeeType
  public extraOtherFees: OtherFeeType[] | any

  public shavingFees: BeddingFeeType
  public strawFees: BeddingFeeType
  public extraBeddingFees: BeddingFeeType[] | any

  public coastalHay: FeedFeesType
  public timothyHay: FeedFeesType
  public alfalfa: FeedFeesType
  public TnA: FeedFeesType
  public extraFeedFees: FeedFeesType[] | any

  public registrationLimit: RegistrationLimitType
  public rideTimeRequirement: RideTimeRequirementType
  public qualifyingClasses: QualifyingClassesType

  public created: Date
  public modified: Date

  public constructor(obj?: IEventFees) {
    super()
    this.v = obj?.v ?? 1
    this.id = obj?.id ?? null
    this.owner = obj?.owner ?? null
    this.paymentOption = obj?.paymentOption ?? null

    this.temporaryStallsFee = obj?.temporaryStallsFee
      ? {
          category: this.getString('stalls'),
          title: this.getString('Temporary stalls'),
          sold: this.getNum(obj?.temporaryStallsFee?.sold),
          uuid: this.getUuid(obj?.temporaryStallsFee?.uuid),
          note: this.getString(obj?.temporaryStallsFee?.note),
          status: this.getBool(obj?.temporaryStallsFee?.status),
          duration: selectObjToString(obj?.temporaryStallsFee?.duration),
          available: this.getNum(obj?.temporaryStallsFee?.available),
          cost: this.getNum(obj?.temporaryStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.temporaryStallsFee?.actualCost),
        }
      : {
          sold: 0,
          status: false,
          title: 'Temporary stalls',
          category: 'stalls',
          available: 0,
          uuid: this.getUuid(),
          note: this.getString(),
          duration: this.getString(),
          cost: this.getNum(obj?.temporaryStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.temporaryStallsFee?.actualCost),
        }

    this.permanentStallsFee = obj?.permanentStallsFee
      ? {
          category: 'stalls',
          title: this.getString('Permanent stalls'),
          sold: this.getNum(obj?.permanentStallsFee?.sold),
          uuid: this.getUuid(obj?.permanentStallsFee?.uuid),
          note: this.getString(obj?.permanentStallsFee?.note),
          status: this.getBool(obj?.permanentStallsFee?.status),
          duration: this.getString(obj?.permanentStallsFee?.duration),
          available: this.getNum(obj?.permanentStallsFee?.available),
          cost: this.getNum(obj?.permanentStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.permanentStallsFee?.actualCost),
        }
      : {
          status: false,
          title: 'Permanent stalls',
          category: 'stalls',
          sold: 0,
          available: 0,
          uuid: uuidv4(),
          note: this.getString(),
          duration: '',
          cost: this.getNum(obj?.permanentStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.permanentStallsFee?.actualCost),
        }

    this.privateStallsFee = obj?.privateStallsFee
      ? {
          category: 'stalls',
          note: this.getString(obj?.privateStallsFee?.note),
          title: this.getString('Private stalls'),
          sold: this.getNum(obj?.privateStallsFee?.sold),
          uuid: this.getUuid(obj?.privateStallsFee?.uuid),
          status: this.getBool(obj?.privateStallsFee?.status),
          duration: this.getString(obj?.privateStallsFee?.duration),
          available: this.getNum(obj?.privateStallsFee?.available),
          cost: this.getNum(obj?.privateStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.privateStallsFee?.actualCost),
        }
      : {
          status: false,
          category: 'stalls',
          title: 'Private stalls',
          sold: 0,
          available: 0,
          duration: '',
          uuid: uuidv4(),
          note: this.getString(),
          cost: this.getNum(obj?.privateStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.privateStallsFee?.actualCost),
        }

    this.circuitStallsFee = obj?.circuitStallsFee
      ? {
          category: 'stalls',
          note: this.getString(obj?.circuitStallsFee?.note),
          title: this.getString('Circuit stalls'),
          sold: this.getNum(obj?.circuitStallsFee?.sold),
          uuid: this.getUuid(obj?.circuitStallsFee?.uuid),
          status: this.getBool(obj?.circuitStallsFee?.status),
          duration: this.getString(obj?.circuitStallsFee?.duration),
          available: this.getNum(obj?.circuitStallsFee?.available),
          cost: this.getNum(obj?.circuitStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.circuitStallsFee?.actualCost),
        }
      : {
          status: false,
          category: 'stalls',
          title: 'Circuit stalls',
          sold: 0,
          available: 0,
          duration: '',
          uuid: uuidv4(),
          note: this.getString(),
          cost: this.getNum(obj?.circuitStallsFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.circuitStallsFee?.actualCost),
        }

    this.muckFee = obj?.muckFee
      ? {
          category: 'stalls',
          note: this.getString(obj?.muckFee?.note),
          title: this.getString('Muck Fee'),
          sold: this.getNum(obj?.muckFee?.sold),
          uuid: this.getUuid(obj?.muckFee?.uuid),
          status: this.getBool(obj?.muckFee?.status),
          duration: this.getString(obj?.muckFee?.duration),
          available: this.getNum(obj?.muckFee?.available),
          cost: this.getNum(obj?.muckFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.muckFee?.actualCost),
        }
      : {
          status: false,
          category: 'stalls',
          title: 'Muck Fee',
          available: 0,
          sold: 0,
          duration: '',
          uuid: uuidv4(),
          note: this.getString(),
          cost: this.getNum(obj?.muckFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.muckFee?.actualCost),
        }

    this.extraStallFees = Array.isArray(obj?.extraStallFees)
      ? obj?.extraStallFees.map((curr) => {
          return {
            category: 'stalls',
            note: this.getString(curr?.note),
            title: this.getString(curr.title),
            sold: this.getNum(curr?.sold),
            uuid: this.getUuid(curr?.uuid),
            status: this.getBool(curr?.status),
            duration: this.getString(curr?.duration),
            available: this.getNum(curr?.available),
            cost: this.getNum(curr?.actualCost, obj?.paymentOption),
            actualCost: this.getNum(curr?.actualCost),
          }
        })
      : []

    this.registrationFees =
      Array.isArray(obj?.registrationFees) && !!obj?.registrationFees.length
        ? obj?.registrationFees.map((curr) => {
            return {
              uuid: this.getUuid(curr?.uuid),
              order: curr?.order ?? null,
              name: this.getUuid(curr?.name),
              note: this.getString(curr?.note),
              category: 'registrations',
              cost: this.getNum(curr?.actualCost, obj?.paymentOption),
              actualCost: this.getNum(curr?.actualCost),
              increment: this.getUuid(curr?.increment),
              scoreCard: this.getString(curr?.scoreCard),
              discipline: curr?.discipline ?? null,
              judges: curr?.judges ?? [],
              prizeMoney: this.getNum(curr?.prizeMoney),
              isAsc: this.getBool(curr?.isAsc),
              qualifyingClass: curr?.qualifyingClass ?? '',
              location: curr?.location ?? '',
              startDate: this.utcTimestamp({
                key: 'startDate' as any,
                isTimestamp: true,
                value: curr?.startDate,
              }),
              startTimeHours: curr?.startTimeHours ?? '00',
              startTimeMinutes: curr?.startTimeMinutes ?? '00',
              startTimeFormat: curr?.startTimeFormat ?? 'AM',
              tag: curr?.tag ?? '',
              children: curr?.children?.[0]
                ? curr.children.map((children) => ({
                    uuid: this.getUuid(children?.uuid),
                    location: children?.location ?? '',
                    startDate: this.utcTimestamp({
                      key: 'startDate' as any,
                      isTimestamp: true,
                      value: children?.startDate,
                    }),
                    startTimeHours: children?.startTimeHours ?? '00',
                    startTimeMinutes: children?.startTimeMinutes ?? '00',
                    startTimeFormat: children?.startTimeFormat ?? 'AM',
                    tag: children?.tag ?? '',
                    order: children?.order ?? null,
                    name: this.getUuid(children?.name),
                    note: this.getString(children?.note),
                    category: 'registrations',
                    cost: this.getNum(children?.actualCost, obj?.paymentOption),
                    actualCost: this.getNum(children?.actualCost),
                    increment: this.getUuid(children?.increment),
                    scoreCard: this.getString(children?.scoreCard),
                    discipline: children?.discipline ?? null,
                    judges: children?.judges ?? [],
                    prizeMoney: this.getNum(children?.prizeMoney),
                    isAsc: this.getBool(children?.isAsc),
                    qualifyingClass: children?.qualifyingClass ?? '',
                  }))
                : [],
            }
          })!
        : []

    this.beforeClosingDateRefund = obj?.beforeClosingDateRefund
      ? {
          category: this.getString('refund'),
          name: this.getString('Before closing date'),
          uuid: this.getUuid(obj?.beforeClosingDateRefund?.uuid),
          note: this.getString(obj?.beforeClosingDateRefund?.note),
          status: this.getBool(obj?.beforeClosingDateRefund?.status),
          percentage: this.getNum(obj?.beforeClosingDateRefund?.percentageAlias),
          percentageAlias: `${this.getNum(obj?.beforeClosingDateRefund?.percentageAlias)}%`,
        }
      : {
          status: false,
          uuid: uuidv4(),
          category: this.getString('refund'),
          name: this.getString('Before closing date'),
          percentage: 0,
          percentageAlias: '0%',
          note: '',
        }

    this.afterClosingDateRefund = obj?.afterClosingDateRefund
      ? {
          category: this.getString('refund'),
          name: this.getString('After closing date'),
          uuid: this.getUuid(obj?.afterClosingDateRefund?.uuid),
          note: this.getString(obj?.afterClosingDateRefund?.note),
          status: this.getBool(obj?.afterClosingDateRefund?.status),
          percentage: this.getNum(obj?.afterClosingDateRefund?.percentageAlias),
          percentageAlias: `${this.getNum(obj?.afterClosingDateRefund?.percentageAlias)}%`,
        }
      : {
          status: false,
          percentage: 0,
          percentageAlias: '0%',
          uuid: this.getUuid(),
          category: this.getString('refund'),
          name: this.getString('After closing date'),
          note: this.getString(''),
        }

    this.showCancellationRefundWeather = obj?.showCancellationRefundWeather
      ? {
          category: this.getString('refund'),
          name: this.getString('Show cancellation - weather'),
          uuid: this.getUuid(obj?.showCancellationRefundWeather?.uuid),
          note: this.getString(obj?.showCancellationRefundWeather?.note),
          status: this.getBool(obj?.showCancellationRefundWeather?.status),
          percentage: this.getNum(obj?.showCancellationRefundWeather?.percentageAlias),
          percentageAlias: `${this.getNum(obj?.showCancellationRefundWeather?.percentageAlias)}%`,
        }
      : {
          status: false,
          percentage: 0,
          percentageAlias: '0%',
          uuid: this.getUuid(),
          note: this.getString(),
          category: this.getString('refund'),
          name: this.getString('Show cancellation - weather'),
        }

    this.showCancellationRefundBioSecurity = obj?.showCancellationRefundBioSecurity
      ? {
          category: this.getString('refund'),
          name: this.getString('Show cancellation - biosecurity'),
          uuid: this.getUuid(obj?.showCancellationRefundBioSecurity?.uuid),
          note: this.getString(obj?.showCancellationRefundBioSecurity?.note),
          status: this.getBool(obj?.showCancellationRefundBioSecurity?.status),
          percentage: this.getNum(obj?.showCancellationRefundBioSecurity?.percentageAlias),
          percentageAlias: `${this.getNum(obj?.showCancellationRefundBioSecurity?.percentageAlias)}%`,
        }
      : {
          status: false,
          uuid: uuidv4(),
          name: 'Show cancellation - biosecurity',
          category: 'refund',
          percentage: 0,
          percentageAlias: '0%',
          note: this.getString(),
        }

    this.extraRefunds = Array.isArray(obj?.extraRefunds)
      ? obj?.extraRefunds.map((curr) => {
          return {
            category: this.getString('refund'),
            name: this.getString(curr?.name),
            uuid: this.getUuid(curr?.uuid),
            note: this.getString(curr?.note),
            status: this.getBool(curr?.status),
            percentage: this.getNum(curr?.percentageAlias),
            percentageAlias: `${this.getNum(curr?.percentageAlias)}%`,
          }
        })
      : []

    this.USEFFee = obj?.USEFFee
      ? {
          name: this.getString('Office fee'),
          category: this.getString('governance'),
          note: this.getString(obj?.USEFFee?.note),
          sold: this.getNum(obj?.USEFFee?.sold),
          uuid: this.getUuid(obj?.USEFFee?.uuid),
          status: this.getBool(obj?.USEFFee?.status),
          duration: this.getString(obj?.USEFFee?.duration),
          available: this.getNum(obj?.USEFFee?.available),
          cost: this.getNum(obj?.USEFFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USEFFee?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          name: this.getString('Office fee'),
          category: this.getString('governance'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.USEFFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USEFFee?.actualCost),
        }

    this.USEFDrugFee = obj?.USEFDrugFee
      ? {
          name: this.getString('Ship in / Ground fee'),
          category: this.getString('governance'),
          note: this.getString(obj?.USEFDrugFee?.note),
          sold: this.getNum(obj?.USEFDrugFee?.sold),
          uuid: this.getUuid(obj?.USEFDrugFee?.uuid),
          status: this.getBool(obj?.USEFDrugFee?.status),
          duration: this.getString(obj?.USEFDrugFee?.duration),
          available: this.getNum(obj?.USEFDrugFee?.available),
          cost: this.getNum(obj?.USEFDrugFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USEFDrugFee?.actualCost),
        }
      : {
          status: false,
          uuid: uuidv4(),
          name: this.getString('Ship in / Ground fee'),
          category: this.getString('governance'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.USEFDrugFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USEFDrugFee?.actualCost),
        }

    this.USEFShowPassFee = obj?.USEFShowPassFee
      ? {
          name: this.getString('Ambulance fee'),
          category: this.getString('governance'),
          note: this.getString(obj?.USEFShowPassFee?.note),
          sold: this.getNum(obj?.USEFShowPassFee?.sold),
          uuid: this.getUuid(obj?.USEFShowPassFee?.uuid),
          status: this.getBool(obj?.USEFShowPassFee?.status),
          duration: this.getString(obj?.USEFShowPassFee?.duration),
          available: this.getNum(obj?.USEFShowPassFee?.available),
          cost: this.getNum(obj?.USEFShowPassFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USEFShowPassFee?.actualCost),
        }
      : {
          status: false,
          uuid: uuidv4(),
          name: 'Ambulance fee',
          category: 'governance',
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.USEFShowPassFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USEFShowPassFee?.actualCost),
        }

    this.USDFFee = obj?.USDFFee
      ? {
          name: this.getString('Night Watch'),
          category: this.getString('governance'),
          note: this.getString(obj?.USDFFee?.note),
          sold: this.getNum(obj?.USDFFee?.sold),
          uuid: this.getUuid(obj?.USDFFee?.uuid),
          status: this.getBool(obj?.USDFFee?.status),
          duration: this.getString(obj?.USDFFee?.duration),
          available: this.getNum(obj?.USDFFee?.available),
          cost: this.getNum(obj?.USDFFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USDFFee?.actualCost),
        }
      : {
          status: false,
          uuid: uuidv4(),
          name: this.getString('Night Watch'),
          category: this.getString('governance'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.USDFFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.USDFFee?.actualCost),
        }

    this.extraGovernanceFees = Array.isArray(obj?.extraGovernanceFees)
      ? obj?.extraGovernanceFees.map((curr) => {
          return {
            name: this.getString(curr.name),
            category: this.getString('governance'),
            note: this.getString(curr?.note),
            sold: this.getNum(curr?.sold),
            uuid: this.getUuid(curr?.uuid),
            status: this.getBool(curr?.status),
            duration: this.getString(curr?.duration),
            available: this.getNum(curr?.available),
            cost: this.getNum(curr?.actualCost, curr?.paymentOption),
            actualCost: this.getNum(curr?.actualCost),
          }
        })
      : []

    this.paddockFee = obj?.paddockFee
      ? {
          title: this.getString('Paddock'),
          category: this.getString('other'),
          note: this.getString(obj?.paddockFee?.note),
          sold: this.getNum(obj?.paddockFee?.sold),
          uuid: this.getUuid(obj?.paddockFee?.uuid),
          status: this.getBool(obj?.paddockFee?.status),
          duration: this.getString(obj?.paddockFee?.duration),
          available: this.getNum(obj?.paddockFee?.available),
          cost: this.getNum(obj?.paddockFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.paddockFee?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          title: this.getString('Paddock'),
          category: this.getString('other'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.paddockFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.paddockFee?.actualCost),
        }

    this.RVHookupFee = obj?.RVHookupFee
      ? {
          title: this.getString('RV hookup'),
          category: this.getString('other'),
          note: this.getString(obj?.RVHookupFee?.note),
          sold: this.getNum(obj?.RVHookupFee?.sold),
          uuid: this.getUuid(obj?.RVHookupFee?.uuid),
          status: this.getBool(obj?.RVHookupFee?.status),
          duration: this.getString(obj?.RVHookupFee?.duration),
          available: this.getNum(obj?.RVHookupFee?.available),
          cost: this.getNum(obj?.RVHookupFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.RVHookupFee?.actualCost),
        }
      : {
          status: false,
          uuid: uuidv4(),
          category: 'other',
          title: 'RV hookup',
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.RVHookupFee?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.RVHookupFee?.actualCost),
        }

    this.extraOtherFees = Array.isArray(obj?.extraOtherFees)
      ? obj?.extraOtherFees.map((curr) => {
          return {
            title: this.getString(curr?.title),
            category: this.getString('other'),
            sold: this.getNum(curr?.sold),
            note: this.getString(curr?.note),
            uuid: this.getUuid(curr?.uuid),
            status: this.getBool(curr?.status),
            duration: this.getString(curr?.duration),
            available: this.getNum(curr?.available),
            cost: this.getNum(curr?.actualCost, obj?.paymentOption),
            actualCost: this.getNum(curr?.actualCost),
          }
        })
      : []

    this.shavingFees = obj?.shavingFees
      ? {
          title: this.getString('Shaving'),
          category: this.getString('bedding'),
          sold: this.getNum(obj?.shavingFees?.sold),
          note: this.getString(obj?.shavingFees?.note),
          uuid: this.getUuid(obj?.shavingFees?.uuid),
          status: this.getBool(obj?.shavingFees?.status),
          duration: this.getString(obj?.shavingFees?.duration),
          available: this.getNum(obj?.shavingFees?.available),
          cost: this.getNum(obj?.shavingFees?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.shavingFees?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          title: this.getString('Shaving'),
          category: this.getString('bedding'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.shavingFees?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.shavingFees?.actualCost),
        }

    this.strawFees = obj?.strawFees
      ? {
          title: this.getString('Straw'),
          category: this.getString('bedding'),
          sold: this.getNum(obj?.strawFees?.sold),
          note: this.getString(obj?.strawFees?.note),
          uuid: this.getUuid(obj?.strawFees?.uuid),
          status: this.getBool(obj?.strawFees?.status),
          duration: this.getString(obj?.strawFees?.duration),
          available: this.getNum(obj?.strawFees?.available),
          cost: this.getNum(obj?.strawFees?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.strawFees?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          title: this.getString('Straw'),
          category: this.getString('bedding'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.strawFees?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.strawFees?.actualCost),
        }

    this.extraBeddingFees = Array.isArray(obj?.extraBeddingFees)
      ? obj?.extraBeddingFees.map((curr) => {
          return {
            title: this.getString(curr?.title),
            category: this.getString('bedding'),
            sold: this.getNum(curr?.sold),
            uuid: this.getUuid(curr?.uuid),
            note: this.getString(curr?.note),
            status: this.getBool(curr?.status),
            duration: this.getString(curr?.duration),
            available: this.getNum(curr?.available),
            cost: this.getNum(curr?.actualCost, obj?.paymentOption),
            actualCost: this.getNum(curr?.actualCost),
          }
        })
      : []

    this.coastalHay = obj?.coastalHay
      ? {
          title: this.getString('Coastal hay'),
          category: this.getString('feed'),
          sold: this.getNum(obj?.coastalHay?.sold),
          uuid: this.getUuid(obj?.coastalHay?.uuid),
          note: this.getString(obj?.coastalHay?.note),
          status: this.getBool(obj?.coastalHay?.status),
          duration: this.getString(obj?.coastalHay?.duration),
          available: this.getNum(obj?.coastalHay?.available),
          cost: this.getNum(obj?.coastalHay?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.coastalHay?.actualCost),
        }
      : {
          status: false,
          uuid: uuidv4(),
          category: 'feed',
          title: 'Coastal hay',
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.coastalHay?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.coastalHay?.actualCost),
        }

    this.timothyHay = obj?.timothyHay
      ? {
          title: this.getString('Timothy hay'),
          category: this.getString('feed'),
          sold: this.getNum(obj?.timothyHay?.sold),
          uuid: this.getUuid(obj?.timothyHay?.uuid),
          note: this.getString(obj?.timothyHay?.note),
          status: this.getBool(obj?.timothyHay?.status),
          duration: this.getString(obj?.timothyHay?.duration),
          available: this.getNum(obj?.timothyHay?.available),
          cost: this.getNum(obj?.timothyHay?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.timothyHay?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          title: this.getString('Timothy hay'),
          category: this.getString('feed'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.timothyHay?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.timothyHay?.actualCost),
        }

    this.alfalfa = obj?.alfalfa
      ? {
          title: this.getString('Alfalfa hay'),
          category: this.getString('feed'),
          sold: this.getNum(obj?.alfalfa?.sold),
          uuid: this.getUuid(obj?.alfalfa?.uuid),
          note: this.getString(obj?.alfalfa?.note),
          status: this.getBool(obj?.alfalfa?.status),
          duration: this.getString(obj?.alfalfa?.duration),
          available: this.getNum(obj?.alfalfa?.available),
          cost: this.getNum(obj?.alfalfa?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.alfalfa?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          title: this.getString('Alfalfa hay'),
          category: this.getString('feed'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.alfalfa?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.alfalfa?.actualCost),
        }

    this.TnA = obj?.TnA
      ? {
          title: this.getString('TnA hay'),
          category: this.getString('feed'),
          sold: this.getNum(obj?.TnA?.sold),
          uuid: this.getUuid(obj?.TnA?.uuid),
          note: this.getString(obj?.TnA?.note),
          status: this.getBool(obj?.TnA?.status),
          duration: this.getString(obj?.TnA?.duration),
          available: this.getNum(obj?.TnA?.available),
          cost: this.getNum(obj?.TnA?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.TnA?.actualCost),
        }
      : {
          status: false,
          uuid: this.getUuid(),
          title: this.getString('TnA hay'),
          category: this.getString('feed'),
          available: 0,
          sold: 0,
          duration: '',
          note: this.getString(),
          cost: this.getNum(obj?.TnA?.actualCost, obj?.paymentOption),
          actualCost: this.getNum(obj?.TnA?.actualCost),
        }

    this.extraFeedFees = Array.isArray(obj?.extraFeedFees)
      ? obj?.extraFeedFees.map((curr) => {
          return {
            title: this.getString(curr?.title),
            category: this.getString('feed'),
            sold: this.getNum(curr?.sold),
            uuid: this.getUuid(curr?.uuid),
            note: this.getString(curr?.note),
            status: this.getBool(curr?.status),
            duration: this.getString(curr?.duration),
            available: this.getNum(curr?.available),
            cost: this.getNum(curr?.actualCost, obj?.paymentOption),
            actualCost: this.getNum(curr?.actualCost),
          }
        })
      : []

    this.registrationLimit = obj?.registrationLimit ?? { isEnable: false, spots: null }
    this.rideTimeRequirement = obj?.rideTimeRequirement ?? { isEnable: false, minutes: null }
    this.qualifyingClasses = obj?.qualifyingClasses ?? { isEnable: false, items: [] }

    this.created = this.utcTimestamp({
      key: 'created',
      isTimestamp: true,
      value: obj?.created,
    })

    this.modified = this.utcTimestamp({
      key: 'modified',
      isTimestamp: true,
      changeOnUpdate: true,
      value: obj?.modified,
    })
  }

  getBool(bool: any) {
    if (typeof bool !== 'boolean') return false
    return bool
  }

  getUuid(uuid?: any) {
    let uuid_
    if (typeof uuid !== 'string') uuid_ = uuidv4()
    else uuid_ = uuid
    uuid_ = uuid_.trim()
    return uuid_
  }

  getString(str?: any) {
    let strToReturn

    if (typeof str === 'object') strToReturn = selectObjToString(str)
    else if (typeof str !== 'string') strToReturn = ''
    else strToReturn = str

    strToReturn = strToReturn.trim()
    return strToReturn
  }

  getNum(number: any, paymentOption?: IEventFees['paymentOption']) {
    const incrementBy = getIncrement(paymentOption || null)
    if (!number) return 0

    let num

    if (typeof number === 'string') num = Number(number.replace(/[^0-9\..]+/gi, ''))
    else num = Number(number)

    if (isNaN(num)) return 0 + (incrementBy ? incrementBy : 0)

    num = num + num * (incrementBy ? incrementBy : 0)

    return Number(num?.toFixed(2))
  }

  static fromFirestoreDoc(doc: any) {
    return new EventFeesModel({
      id: doc.id,
      ...doc.data(),
    })
  }

  static validationSchema() {
    return yup.object().shape({
      registrationFees: yup.array(
        yup.object().shape({
          name: yup.string().required('Name is required'),
          children: yup.array(
            yup.object().shape({
              name: yup.string().required('Name is required'),
            })
          ),
        })
      ),
      qualifyingClasses: yup.object().shape({
        items: yup.array(
          yup.object().shape({
            name: yup.string().max(20, 'Max 20 characters').required('Name is required'),
          })
        ),
      }),
    })
  }

  static validationSchemaSchedulingByClass() {
    return yup.object().shape({
      registrationFees: yup.array(
        yup.object().shape({
          cost: yup.string().required('Price is a required'),
          name: yup.string().required('Name is required'),
          children: yup.array(
            yup.object().shape({
              cost: yup.string().required('Price is a required'),
              name: yup.string().required('Name is required'),
              location: yup
                .string()
                .transform((value) => {
                  return value.trim()
                })
                .required('Location is required'),
              tag: yup
                .string()
                .transform((value) => {
                  return value.trim()
                })
                .required('Tag is required'),
              startDate: yup
                .date()
                .nullable()
                .transform((value, originalValue) => {
                  if (!moment(originalValue).isValid()) return null
                  return value
                })
                .typeError('Entered date must be valid')
                .required('Day is a required field'),
            })
          ),
          location: yup
            .string()
            .transform((value) => {
              return value.trim()
            })
            .required('Location is required'),
          tag: yup
            .string()
            .transform((value) => {
              return value.trim()
            })
            .required('Tag is required'),
          startDate: yup
            .date()
            .nullable()
            .transform((value, originalValue) => {
              if (!moment(originalValue).isValid()) return null
              return value
            })
            .typeError('Entered date must be valid')
            .required('Day is a required field'),
        })
      ),
    })
  }
  static validationSchemaSchedulingByRider() {
    return yup.object().shape({
      registrationFees: yup.array(
        yup.object().shape({
          cost: yup.string().required('Price is a required'),
          name: yup.string().required('Name is required'),
          children: yup.array(
            yup.object().shape({
              cost: yup.string().required('Price is a required'),
              name: yup.string().required('Name is required'),
              location: yup
                .string()
                .transform((value) => {
                  return value.trim()
                })
                .required('Location is required'),
              increment: yup
                .string()
                .transform((value) => {
                  return value?.trim()
                })
                .required('Increment is required'),
            })
          ),
          location: yup
            .string()
            .transform((value) => {
              return value.trim()
            })
            .required('Location is required'),
          increment: yup
            .string()
            .transform((value) => {
              return value?.trim()
            })
            .required('Increment is required'),
        })
      ),
    })
  }
}
